/**
 * Defensible printable PDF report for a process train.
 *
 * Water-industry standard layout (AWWA / IWA / Mott MacDonald style):
 *   – Document control + revision history
 *   – Executive summary (KPI compliance against RO design basis)
 *   – Design basis (RO membrane envelope)
 *   – Influent water characterisation
 *   – Process description in prose form, one section per stage
 *   – Engineering equations & correlations (rendered legibly)
 *   – Cumulative material balance through the train
 *   – Recommendations driven by the simulation outcome
 *   – Risk register
 *   – Standards & references
 *   – Limitations
 *
 * All text is word-wrapped via splitTextToSize. Y-cursor advance is
 * derived from the actual number of wrapped lines so nothing bleeds
 * past the page margin. Equations render in monospace inside a tinted
 * panel with the symbol legend underneath.
 *
 * Prices are intentionally omitted — this report focuses on engineering
 * description and recommendations only.
 */
import { jsPDF } from 'jspdf';

/**
 * Sanitise strings for jsPDF's default Helvetica WinAnsi encoding.
 * Unicode subscripts (₀–₉), superscripts, Greek (Δ, μ), bullets, en/em dashes,
 * smart quotes and arrows are not in WinAnsi — they render as wide-spaced
 * fallback glyphs ("C a t a l y t i c") which destroys the table layout.
 * Map every such character to its WinAnsi-safe equivalent.
 */
function ascii(s: string): string {
  if (!s) return s;
  const subs: Record<string, string> = {
    '₀':'0','₁':'1','₂':'2','₃':'3','₄':'4','₅':'5','₆':'6','₇':'7','₈':'8','₉':'9',
    '⁰':'0','¹':'1','²':'2','³':'3','⁴':'4','⁵':'5','⁶':'6','⁷':'7','⁸':'8','⁹':'9',
    'Δ':'D','δ':'d','μ':'u','µ':'u','α':'a','β':'b','γ':'g','Σ':'S','σ':'s','π':'pi',
    'Ω':'ohm','θ':'th','λ':'lam','ρ':'rho','τ':'tau','φ':'phi','χ':'chi','ψ':'psi',
    '°':' deg','±':'+/-','×':'x','÷':'/','·':'-','•':'-','–':'-','—':'-',
    '“':'"','”':'"','‘':"'",'’':"'",'≤':'<=','≥':'>=','≠':'!=','≈':'~','∞':'inf',
    '→':'->','←':'<-','↑':'^','↓':'v','⇒':'=>','⇐':'<=',
    '√':'sqrt','∑':'Sum','∏':'Prod','∫':'int','∆':'D',
  };
  return s.replace(/[^\x20-\x7E]/g, ch => subs[ch] ?? '');
}

export interface TrainReportInput {
  trainName: string;
  description?: string;
  customerName?: string;
  siteName?: string;
  preparedBy?: string;
  preparedFor?: string;
  generatedAt: string;
  influent: {
    flow: number; fe: number; mn: number; ph: number; alkalinity: number;
    tss: number; turbidity: number; sdi: number; doIn: number; temp: number; hardness: number;
  };
  stages: Array<{
    order: number;
    key: string;
    name: string;
    description: string;
    paramsSummary: Array<{ label: string; value: string }>;
    inWater: Record<string, number>;
    outWater: Record<string, number>;
    notes: string;
    capexGBP: number;                                   // ignored — kept for type compat
    opexGBPperYear: number;                             // ignored — kept for type compat
    physics: Array<{ label: string; equation?: string; detail: string }>;
    validation: Array<{ label: string; status: 'pass' | 'warn' | 'fail'; detail: string }>;
  }>;
  finalWater: { fe: number; mn: number; sdi: number; tss: number; turbidity: number; doIn: number };
  totalCapexGBP: number;                                 // ignored
  totalOpexAnnualGBP: number;                            // ignored
  roMembrane: { brand: string; model: string; classLabel: string; nominalLifeYears: number; elementPriceGBP: number; feMaxMgL: number; mnMaxMgL: number; sdiMax: number; freeChlorineMaxMgL: number };
  roLifeYears: number;
  roCost10yrGBP: number;                                 // ignored
  roElementsPerVessel: number;
  meetsRO: boolean;
}

/* ── Layout constants ────────────────────────────────────── */
const PAGE_W = 210;
const PAGE_H = 297;
const M = 18;
const CONTENT_W = PAGE_W - 2 * M;
const BOTTOM_LIMIT = PAGE_H - 22;

const C = {
  BRAND: '#cda42e', INK: '#0f172a', INK2: '#1e293b', MUTED: '#475569',
  RULE: '#cbd5e1',  GOOD: '#059669', WARN: '#d97706', BAD: '#dc2626',
  PANEL: '#f1f5f9', PANELB: '#cbd5e1',
  EQBG: '#0f172a',  EQ: '#e2e8f0',
};

function rgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}
const setFill = (doc: jsPDF, hex: string) => { const [r, g, b] = rgb(hex); doc.setFillColor(r, g, b); };
const setDraw = (doc: jsPDF, hex: string) => { const [r, g, b] = rgb(hex); doc.setDrawColor(r, g, b); };
const setText = (doc: jsPDF, hex: string) => { const [r, g, b] = rgb(hex); doc.setTextColor(r, g, b); };
const setFont = (doc: jsPDF, family: 'helvetica' | 'courier', style: 'normal' | 'bold' | 'italic', size: number) => {
  doc.setFont(family, style);
  doc.setFontSize(size);
};

class Layout {
  doc: jsPDF; y = 36; pageNo = 1; totalPages = 1;
  sectionTitle = ''; trainName: string; projectCode: string; revision: string; date: string;

  constructor(doc: jsPDF, opts: { trainName: string; projectCode: string; revision: string; date: string }) {
    this.doc = doc;
    this.trainName = ascii(opts.trainName);
    this.projectCode = ascii(opts.projectCode);
    this.revision = ascii(opts.revision);
    this.date = ascii(opts.date);
  }

  startPage(title: string) { this.sectionTitle = ascii(title); this.y = 36; this.drawHeader(); this.drawFooter(); }

  drawHeader() {
    const { doc } = this;
    setFill(doc, C.BRAND); doc.rect(0, 0, PAGE_W, 6, 'F');
    setText(doc, C.INK); setFont(doc, 'helvetica', 'bold', 11);
    doc.text(this.sectionTitle, M, 14);
    setText(doc, C.MUTED); setFont(doc, 'helvetica', 'normal', 8.5);
    const trainWrapped = doc.splitTextToSize(this.trainName, CONTENT_W * 0.55);
    doc.text(trainWrapped[0] ?? '', M, 20);
    setFont(doc, 'helvetica', 'normal', 8);
    doc.text(`Project ${this.projectCode}`, PAGE_W - M, 14, { align: 'right' });
    doc.text(`Rev. ${this.revision}  ·  ${this.date}`, PAGE_W - M, 19, { align: 'right' });
    doc.text(`Page ${this.pageNo} of ${this.totalPages}`, PAGE_W - M, 24, { align: 'right' });
    setDraw(doc, C.RULE); doc.setLineWidth(0.3);
    doc.line(M, 28, PAGE_W - M, 28);
  }

  drawFooter() {
    const { doc } = this;
    setDraw(doc, C.RULE); doc.line(M, PAGE_H - 14, PAGE_W - M, PAGE_H - 14);
    setText(doc, C.MUTED); setFont(doc, 'helvetica', 'normal', 7);
    const t = ascii('Reynolds & Bauhm Ltd · Co. 10436543 · Smart Tool 2.0 · Engineering estimates based on industry-standard correlations and manufacturer published data. Detailed engineering, on-site verification and pilot validation are required before fabrication or supply.');
    const wrapped = doc.splitTextToSize(t, CONTENT_W);
    doc.text(wrapped, M, PAGE_H - 9);
  }

  /** Advance Y, paginating if needed. `needed` is the height in mm we want to reserve. */
  ensureRoom(needed: number) {
    if (this.y + needed > BOTTOM_LIMIT) {
      this.doc.addPage();
      this.pageNo++;
      this.startPage(this.sectionTitle + ' (cont.)');
    }
  }

  addHeading(text: string, level: 1 | 2 | 3 = 1) {
    text = ascii(text);
    const sizes = { 1: 13, 2: 11, 3: 9.5 } as const;
    const gaps  = { 1: 8,  2: 6,  3: 5 } as const;
    this.ensureRoom(gaps[level] + 4);
    setText(this.doc, C.INK); setFont(this.doc, 'helvetica', 'bold', sizes[level]);
    const wrapped = this.doc.splitTextToSize(text, CONTENT_W);
    this.doc.text(wrapped, M, this.y);
    this.y += wrapped.length * (sizes[level] * 0.45) + 1;
    if (level === 1) {
      setDraw(this.doc, C.BRAND); this.doc.setLineWidth(0.6);
      this.doc.line(M, this.y, M + 36, this.y);
      this.y += 3;
    } else {
      this.y += 1;
    }
  }

  /**
   * Wrap a paragraph and advance the cursor by the actual rendered height,
   * page-breaking mid-paragraph if necessary.
   */
  addParagraph(text: string, opts?: { italic?: boolean; bold?: boolean; size?: number; color?: string; indent?: number }) {
    text = ascii(text);
    const size = opts?.size ?? 9.5;
    const lineH = size * 0.45;
    const indent = opts?.indent ?? 0;
    setFont(this.doc, 'helvetica', opts?.italic ? 'italic' : opts?.bold ? 'bold' : 'normal', size);
    setText(this.doc, opts?.color ?? C.INK);
    const wrapped = this.doc.splitTextToSize(text, CONTENT_W - indent);
    for (const line of wrapped) {
      this.ensureRoom(lineH + 0.5);
      this.doc.text(line, M + indent, this.y);
      this.y += lineH;
    }
    this.y += 2;
  }

  addBullet(text: string, opts?: { size?: number }) {
    text = ascii(text);
    const size = opts?.size ?? 9;
    const lineH = size * 0.45;
    setFont(this.doc, 'helvetica', 'normal', size);
    setText(this.doc, C.INK);
    const wrapped = this.doc.splitTextToSize(text, CONTENT_W - 6);
    for (let i = 0; i < wrapped.length; i++) {
      this.ensureRoom(lineH + 0.5);
      if (i === 0) this.doc.text('•', M, this.y);
      this.doc.text(wrapped[i], M + 4, this.y);
      this.y += lineH;
    }
    this.y += 1;
  }

  /** Wrap both key and value so neither bleeds past the margin. */
  addKeyValueRow(key: string, value: string, opts?: { valueColor?: string; bold?: boolean }) {
    key = ascii(key); value = ascii(value);
    const keyW = 64;
    const valW = CONTENT_W - keyW - 2;
    setFont(this.doc, 'helvetica', 'normal', 9);
    setText(this.doc, C.MUTED);
    const keyWrapped = this.doc.splitTextToSize(key, keyW);
    setFont(this.doc, 'helvetica', opts?.bold ? 'bold' : 'normal', 9);
    setText(this.doc, opts?.valueColor ?? C.INK);
    const valWrapped = this.doc.splitTextToSize(value, valW);
    const lineCount = Math.max(keyWrapped.length, valWrapped.length);
    const rowH = lineCount * 4.4;
    this.ensureRoom(rowH + 1.5);
    // Re-set for key (color/font reset because ensureRoom may have started a new page)
    setFont(this.doc, 'helvetica', 'normal', 9);
    setText(this.doc, C.MUTED);
    this.doc.text(keyWrapped, M, this.y);
    setFont(this.doc, 'helvetica', opts?.bold ? 'bold' : 'normal', 9);
    setText(this.doc, opts?.valueColor ?? C.INK);
    this.doc.text(valWrapped, M + keyW + 2, this.y);
    this.y += rowH + 1.5;
  }

  /** A boxed equation panel — monospace expression + plain-language legend below. */
  addEquation(label: string, expression: string, legend: string) {
    label = ascii(label); expression = ascii(expression); legend = ascii(legend);
    const padding = 4;
    const exprFont = 9;
    const legendFont = 8;
    const labelFont = 8.5;
    const exprWrap = this.doc.splitTextToSize(expression, CONTENT_W - 2 * padding);
    const legendWrap = this.doc.splitTextToSize(legend, CONTENT_W - 2 * padding);
    const h = padding + 4 + exprWrap.length * 4 + 3 + legendWrap.length * 3.6 + padding;
    this.ensureRoom(h + 2);
    // Frame
    setFill(this.doc, C.EQBG); setDraw(this.doc, '#334155'); this.doc.setLineWidth(0.4);
    this.doc.roundedRect(M, this.y, CONTENT_W, h, 2, 2, 'FD');
    // Label
    setText(this.doc, C.BRAND); setFont(this.doc, 'helvetica', 'bold', labelFont);
    this.doc.text(label, M + padding, this.y + padding + 2);
    // Expression (monospace, white)
    setText(this.doc, C.EQ); setFont(this.doc, 'courier', 'bold', exprFont);
    this.doc.text(exprWrap, M + padding, this.y + padding + 8);
    // Legend (plain prose, muted)
    setText(this.doc, '#94a3b8'); setFont(this.doc, 'helvetica', 'italic', legendFont);
    this.doc.text(legendWrap, M + padding, this.y + padding + 8 + exprWrap.length * 4 + 3);
    this.y += h + 3;
  }

  addPanel(title: string, body: string, tone: 'good' | 'warn' | 'bad' | 'neutral' = 'neutral') {
    title = ascii(title); body = ascii(body);
    const colour = tone === 'good' ? C.GOOD : tone === 'warn' ? C.WARN : tone === 'bad' ? C.BAD : '#475569';
    const titleWrap = this.doc.splitTextToSize(title, CONTENT_W - 6);
    const bodyWrap  = this.doc.splitTextToSize(body, CONTENT_W - 6);
    const h = 4 + titleWrap.length * 4.5 + 1 + bodyWrap.length * 4 + 4;
    this.ensureRoom(h + 2);
    setFill(this.doc, C.PANEL); setDraw(this.doc, colour); this.doc.setLineWidth(0.6);
    this.doc.roundedRect(M, this.y, CONTENT_W, h, 2, 2, 'FD');
    setText(this.doc, colour); setFont(this.doc, 'helvetica', 'bold', 9);
    this.doc.text(titleWrap, M + 3, this.y + 6);
    setText(this.doc, C.INK); setFont(this.doc, 'helvetica', 'normal', 8.5);
    this.doc.text(bodyWrap, M + 3, this.y + 6 + titleWrap.length * 4.5 + 1);
    this.y += h + 3;
  }

  addTable(headers: string[], rows: string[][], colWidths: number[]) {
    headers = headers.map(ascii);
    rows = rows.map(r => r.map(ascii));
    // Normalise column widths to fit CONTENT_W so nothing bleeds past the page margin
    const sum = colWidths.reduce((a, b) => a + b, 0);
    if (sum > CONTENT_W) {
      const k = CONTENT_W / sum;
      colWidths = colWidths.map(w => w * k);
    }
    const rowH = 5.5;
    const headerH = 6.5;
    this.ensureRoom(headerH + rowH);
    setFill(this.doc, C.PANEL); this.doc.rect(M, this.y - 4, CONTENT_W, headerH, 'F');
    setText(this.doc, C.INK); setFont(this.doc, 'helvetica', 'bold', 8.5);
    let x = M;
    headers.forEach((h, i) => {
      const wrap = this.doc.splitTextToSize(h, colWidths[i] - 2);
      this.doc.text(wrap[0] ?? '', x + 1.5, this.y);
      x += colWidths[i];
    });
    this.y += headerH - 2;
    setDraw(this.doc, C.RULE); this.doc.setLineWidth(0.2);
    this.doc.line(M, this.y - 2, M + CONTENT_W, this.y - 2);
    setFont(this.doc, 'helvetica', 'normal', 8.5);
    rows.forEach(row => {
      // Determine row height from the widest wrapped cell
      const wrappedCells = row.map((cell, i) => this.doc.splitTextToSize(cell, colWidths[i] - 2));
      const lineCount = Math.max(...wrappedCells.map(c => c.length));
      const h = lineCount * 4.2 + 1;
      this.ensureRoom(h + 1);
      x = M;
      setText(this.doc, C.INK);
      wrappedCells.forEach((wrap, i) => {
        this.doc.text(wrap, x + 1.5, this.y + 2);
        x += colWidths[i];
      });
      this.y += h;
      this.doc.line(M, this.y - 1, M + CONTENT_W, this.y - 1);
    });
    this.y += 4;
  }

  addSpacer(h: number) { this.y += h; }
}

/* ── Per-stage prose, equations and recommendations data ─── */
function stageTitle(key: string) {
  switch (key) {
    case 'aeration_column':    return 'Pressurised Aeration Column';
    case 'cascade_aerator':    return 'Gravity Cascade Aerator';
    case 'greensand':          return 'Catalytic Filter (Greensand / Birm / Pyrolusite)';
    case 'uf':                 return 'Ultrafiltration (Hollow-Fibre Membranes)';
    case 'multimedia':         return 'Multimedia Pressure Filter';
    case 'dual_media_filter':  return 'Dual-Media Pressure Filter';
    case 'sand_filter_single': return 'Single-Media Sand Filter';
    case 'gac_filter':         return 'Granular Activated Carbon (GAC)';
    case 'cartridge_polish':   return 'Cartridge Polishing Filter';
    case 'pacl_dosing':        return 'PACl Coagulant Dosing';
    case 'uv_disinfection':    return 'UV Disinfection';
    default: return key;
  }
}

function stagePurpose(key: string): string {
  switch (key) {
    case 'aeration_column':
      return 'A pressurised packed-bed column introduces atmospheric oxygen at a controlled gas-to-liquid ratio, oxidising dissolved Fe(II) to Fe(III) floc and stripping volatile gases (H2S, CO2). The oxidised iron then becomes physically capturable by the downstream filtration stage.';
    case 'cascade_aerator':
      return 'A gravity cascade tower passes the raw water in turbulent free-fall over a series of weir steps. Each step provides a discrete step-change in dissolved oxygen following the Gameson deficit-ratio model, achieving the same Fe(II) to Fe(III) oxidation duty as a pressurised column but without a blower.';
    case 'greensand':
      return 'A pressurised filter loaded with manganese-dioxide-coated media (Greensand Plus, Birm, or Pyrolusite). The MnO2 surface catalytically oxidises and adsorbs both Fe(II) and Mn(II); the resulting solids are then physically retained inside the bed. Continuous KMnO4 dosing regenerates the active sites for Greensand Plus.';
    case 'uf':
      return 'Hollow-fibre ultrafiltration modules with a 0.02 to 0.04 micron nominal pore size act as an absolute physical sieve. Particulate Fe(III), Mn floc, colloids, bacteria and viruses are rejected. Dissolved species (Fe(II), Mn(II), salts) pass through. Production of guaranteed SDI15 < 3 is the principal driver for placing UF as the last barrier before RO.';
    case 'multimedia':
      return 'A graded bed of anthracite on top, silica sand in the middle and garnet at the bottom, operating in down-flow. Captures particles down to approximately 10 to 20 micron via depth filtration. Requires upstream coagulation when influent particles are sub-micron, otherwise removal is modest.';
    case 'dual_media_filter':
      return 'A two-layer bed of anthracite over silica sand. Cheaper than full multimedia but with a coarser cut-off of 15 to 25 micron. Suitable when upstream coagulation generates well-defined flocs.';
    case 'sand_filter_single':
      return 'Conventional single-media silica sand bed. The simplest, lowest-cost pressure filter. Best suited to surface water with already moderate solids, or to applications with strong coagulation upstream.';
    case 'gac_filter':
      return 'A pressurised bed of granular activated carbon. Adsorbs organic compounds (TOC, DBP precursors, taste and odour) and catalytically reduces residual free chlorine (Cl2 + C produces 2 HCl + CO2). Mandatory upstream of RO if any chlorine is present, since chlorine destroys polyamide membranes.';
    case 'cartridge_polish':
      return 'A bank of disposable pleated polypropylene cartridges (typically 5 micron nominal). Acts as a final barrier against floc breakthrough from upstream stages and as an early-warning alarm point: rising differential pressure across the cartridges signals upstream regeneration upset before contamination reaches the RO.';
    case 'pacl_dosing':
      return 'Polyaluminium chloride (PACl, Al13 polymer) is dosed inline with rapid mixing. The cationic aluminium hydrolyses and bridges anionic colloids and sub-micron particles, building them into 20 to 50 micron flocs that the downstream granular filter can capture. Typical dose 0.5 to 2 mg/L as aluminium.';
    case 'uv_disinfection':
      return 'Low-pressure or medium-pressure UV-C reactors at 254 nm inactivate bacteria, viruses and protozoa by photolytic damage to DNA. Sized to deliver the USEPA UVDGM standard 40 mJ/cm2 dose at end-of-lamp life. Does not remove particles, chemistry or metals; only inactivates microbes. Requires clean upstream water (SDI < 3, turbidity < 0.3 NTU).';
    default: return '';
  }
}

/** Water-industry standard equations and design correlations per stage. */
interface StageEquation { label: string; expression: string; legend: string; }
function stageEquations(key: string): StageEquation[] {
  switch (key) {
    case 'aeration_column':
    case 'cascade_aerator':
      return [
        { label: 'Fe(II) oxidation rate (Stumm & Lee, 1961)',
          expression: '-d[Fe(II)]/dt = k * [Fe(II)] * pO2 * [OH-]^2',
          legend: 'k = 1.5E13 M^-2 atm^-1 min^-1 at 20 deg C; squared OH- dependence means the rate increases by a factor of 100 per pH unit. Half-life is 1-2 min at pH 7.8 with adequate DO.' },
        { label: 'DO saturation (Benson & Krause)',
          expression: 'C_sat = 14.6 - 0.394 T + 0.0076 T^2          [mg/L, T in deg C]',
          legend: 'Equilibrium dissolved-oxygen concentration of water at the working temperature. The aerator approach to saturation drives the available driving force for Fe(II) oxidation.' },
        { label: 'Gameson deficit-ratio per cascade step',
          expression: 'r = 1 + 0.5 * a * b * h * (1 + 0.046 T)',
          legend: 'a = water-quality factor (0.85 clean, 1.0 typical, 1.25 hard/saline). b = weir-type factor (1.0 sharp-crested, 1.3 stepped/aerated). h = step fall height in metres. T = water temperature in deg C.' },
      ];
    case 'greensand':
      return [
        { label: 'KMnO4 stoichiometric demand (continuous regeneration)',
          expression: 'KMnO4_dose = 1.0 * [Fe] + 2.0 * [Mn]    (mg/L) x safety factor 1.3',
          legend: 'Greensand Plus continuous-regen demand. Excess KMnO4 must NOT carry over to the RO membranes ("pink water" risk).' },
        { label: 'Service velocity envelope',
          expression: 'v_service = Q / A          5 < v_service < 12 m/h',
          legend: 'Q = treated flow (m3/h). A = total bed cross-sectional area (m2) of duty vessels. Below 5 m/h the catalytic surface contact is insufficient; above 12 m/h iron breaks through the bed.' },
        { label: 'Empty-bed contact time',
          expression: 'EBCT = V_media / Q          target >= 3 min',
          legend: 'V_media = total media volume of duty vessels (m3). Below 3 min the iron-oxidation reaction has insufficient residence to complete before exit.' },
      ];
    case 'uf':
      return [
        { label: 'Membrane flux (transmembrane pressure)',
          expression: 'J = TMP / (mu * R_total)',
          legend: 'J = flux in L/m2 h (LMH). TMP = transmembrane pressure (bar). mu = water dynamic viscosity (Pa s, temperature-dependent). R_total = sum of clean-membrane and fouling resistances. Typical operating flux 30-80 LMH.' },
        { label: 'Temperature correction',
          expression: 'J_20 = J_T * exp(0.0239 * (20 - T))',
          legend: 'Normalises observed flux to 20 deg C reference. Used for comparing operating data across seasons. Lower temperature increases viscosity and raises TMP at the same flux.' },
        { label: 'Recovery and concentration factor',
          expression: 'CF = 1 / (1 - r)          where r = recovery (e.g. 0.92)',
          legend: 'CF = concentration factor of rejected species in the reject stream. At 92% recovery CF = 12.5: rejected particles are concentrated 12-fold, driving the cleaning cycle interval (CEB and CIP).' },
      ];
    case 'multimedia':
    case 'dual_media_filter':
    case 'sand_filter_single':
      return [
        { label: 'Ergun equation (clean-bed head loss)',
          expression: 'dP/L = 150 * mu * u * (1-e)^2 / (e^3 * d^2) + 1.75 * rho * u^2 * (1-e) / (e^3 * d)',
          legend: 'dP/L = pressure gradient (Pa/m). mu = viscosity (Pa s). u = superficial velocity (m/s). e = bed voidage (-). d = mean particle diameter (m). rho = fluid density (kg/m3). The first term dominates at low Reynolds (laminar), the second at high Reynolds.' },
        { label: 'Approach velocity envelope',
          expression: 'v_approach = Q / A          7 < v_approach < 15 m/h',
          legend: 'Below 7 m/h the bed underuses its surface area. Above 15 m/h media migration and floc breakthrough risk become unacceptable per USEPA Filtration Guidance.' },
        { label: 'Richardson-Zaki bed expansion (backwash)',
          expression: 'e_bw = (v_bw / v_terminal)^(1/n)        target 25-30% expansion',
          legend: 'e_bw = expanded voidage. v_bw = backwash velocity (m/s). v_terminal = terminal settling velocity of the bed particle. n = Richardson-Zaki exponent ~4.5 for sand-sized media. Sufficient expansion lifts and rinses captured solids.' },
      ];
    case 'gac_filter':
      return [
        { label: 'Empty-bed contact time',
          expression: 'EBCT = V_bed / Q          10 min minimum for chlorine; 15-20 min for TOC',
          legend: 'V_bed = total GAC volume (m3). Q = flow (m3/h). Below 10 min chlorine breakthrough; below 15 min TOC removal drops below 40%.' },
        { label: 'GAC chlorine capacity (catalytic)',
          expression: 'm_Cl2_per_kg_GAC ~ 5 kg Cl2 / kg GAC (typical life)',
          legend: 'Cl2 + C -> 2 HCl + CO2. Approximate working capacity before chlorine breakthrough. Drives the media replacement interval on chlorinated feed.' },
      ];
    case 'cartridge_polish':
      return [
        { label: 'Flow per cartridge',
          expression: 'Q_per_cart = Q / N          target <= 4 m3/h per 5 micron cartridge',
          legend: 'N = total cartridges in service (housings x cartridges per housing). Above 4 m3/h per cartridge the differential pressure rises rapidly and replacement frequency becomes uneconomic.' },
        { label: 'Replacement trigger (differential pressure)',
          expression: 'dP_replace = dP_clean + 1.0 bar (typical)',
          legend: 'Cartridges are replaced when their differential pressure rises by ~1 bar above the clean baseline. Logged in operator records.' },
      ];
    case 'pacl_dosing':
      return [
        { label: 'Dose-response window',
          expression: '0.5 mg/L <= [Al] <= 2.0 mg/L            for low-turbidity borehole water',
          legend: 'Under-dose lets sub-micron colloids pass; over-dose creates sludge load and post-filter aluminium residual. Optimum sits in a narrow window for clean borehole feeds.' },
        { label: 'Rapid mix energy',
          expression: 'G ~ 600 s^-1 for 30-60 s            then G ~ 50 s^-1 in flocculation',
          legend: 'G = velocity gradient (s^-1). High G during initial mix disperses the coagulant; low G during flocculation grows the flocs to capturable size (20-50 micron) without shearing them apart.' },
      ];
    case 'uv_disinfection':
      return [
        { label: 'Delivered UV dose (Bunsen-Roscoe reciprocity)',
          expression: 'D = I_avg * t          target 40 mJ/cm2 at end-of-lamp-life',
          legend: 'I_avg = mean UV intensity in the chamber (mW/cm2). t = exposure time (s). USEPA UVDGM standard for 4-log Giardia and Cryptosporidium inactivation is 22 mJ/cm2 each; 40 mJ/cm2 provides margin for lamp ageing.' },
        { label: 'Log inactivation',
          expression: 'log10(N/N0) = -D / D_per_log',
          legend: 'D_per_log values per USEPA: E. coli 6.6, Giardia 11, Cryptosporidium 12 mJ/cm2 per log inactivation.' },
        { label: 'Beer-Lambert UV penetration',
          expression: 'I(d) = I0 * UVT^d            d in cm',
          legend: 'UVT = UV transmittance at 254 nm (fraction). Borehole/RO permeate typically 95-98%, surface water 75-90%. Penetration drops exponentially with chamber radius.' },
      ];
    default: return [];
  }
}

/** Diagnose problems and turn them into actionable recommendations. */
function buildRecommendations(d: TrainReportInput): Array<{ priority: 'critical' | 'high' | 'medium' | 'info'; title: string; body: string }> {
  const recs: Array<{ priority: 'critical' | 'high' | 'medium' | 'info'; title: string; body: string }> = [];
  const RO_TARGETS = { fe: 0.05, mn: 0.05, sdi: 3, tss: 5 };

  if (d.finalWater.fe > RO_TARGETS.fe) {
    recs.push({
      priority: 'critical',
      title: 'Final iron concentration exceeds the RO membrane feed limit',
      body: `Simulated final iron of ${d.finalWater.fe.toFixed(3)} mg/L is above the ${RO_TARGETS.fe} mg/L hard limit for the ${d.roMembrane.brand} ${d.roMembrane.model}. ` +
            `This will cause progressive, irreversible iron-oxide fouling of the polyamide active layer. Membrane service life is reduced from a nominal ${d.roMembrane.nominalLifeYears} years to approximately ${d.roLifeYears.toFixed(1)} years on this feed. ` +
            `Recommended action: insert or strengthen an upstream aeration stage (column or cascade tower) followed by a catalytic filter (Greensand Plus duplex) so the dissolved Fe(II) is fully oxidised and captured before reaching the membrane bank. ` +
            `Verify on commissioning that the cumulative iron in the RO feed line is below 0.05 mg/L under all operating scenarios including upstream backwash events.`,
    });
  }
  if (d.finalWater.sdi > RO_TARGETS.sdi) {
    recs.push({
      priority: 'high',
      title: 'Silt density index above the RO design envelope',
      body: `Final SDI15 of ${d.finalWater.sdi.toFixed(1)} is above the RO design ceiling of ${RO_TARGETS.sdi}. ` +
            `Granular media filters cannot physically achieve SDI < 3 without aggressive coagulation; the only stage that guarantees it is ultrafiltration with a sub-micron polymeric membrane. ` +
            `Recommended action: add a UF stage downstream of the catalytic filter. Typical design point is PVDF hollow fibre with 0.02-0.04 micron pore size, design flux 60 LMH, recovery 92% with chemically-enhanced backwash every 8-12 hours and CIP every 30-60 days.`,
    });
  }
  if (d.finalWater.mn > RO_TARGETS.mn) {
    recs.push({
      priority: 'high',
      title: 'Final manganese above the RO membrane feed limit',
      body: `Final Mn of ${d.finalWater.mn.toFixed(3)} mg/L exceeds the ${RO_TARGETS.mn} mg/L limit. ` +
            `Birm media has limited Mn capacity (typically up to 1 mg/L feed). Switch the catalytic stage to Greensand Plus (with KMnO4 regeneration) or Pyrolusite (natural MnO2 ore) for reliable Mn removal up to 10-15 mg/L. ` +
            `Confirm that the upstream aeration stage delivers DO of at least 2 mg/L to drive the catalytic oxidation.`,
    });
  }
  const hasAeration  = d.stages.some(s => s.key === 'aeration_column' || s.key === 'cascade_aerator');
  const hasCatalytic = d.stages.some(s => s.key === 'greensand');
  if (hasCatalytic && !hasAeration) {
    recs.push({
      priority: 'high',
      title: 'Catalytic filter without upstream aeration',
      body: 'The catalytic Fe/Mn filter requires dissolved oxygen to function. Without an upstream aeration stage, Birm media will fail outright and Greensand Plus will consume KMnO4 at an elevated rate, driving up consumable cost and operator load. ' +
            'Recommended action: add either a pressurised aeration column (with blower) or a gravity cascade aerator (no blower required if the available hydraulic head permits a 2-3 m fall) immediately upstream of the catalytic stage.',
    });
  }
  const hasUV = d.stages.some(s => s.key === 'uv_disinfection');
  const hasParticulate = d.stages.some(s => ['uf', 'cartridge_polish', 'multimedia', 'dual_media_filter', 'sand_filter_single'].includes(s.key));
  if (hasUV && !hasParticulate) {
    recs.push({
      priority: 'critical',
      title: 'UV disinfection without upstream particulate removal',
      body: 'UV disinfection requires clear water (SDI < 3, turbidity < 0.3 NTU) so the UV-C reaches the microbes rather than being absorbed or shadowed by suspended particles. ' +
            'Recommended action: insert a UF or cartridge polishing stage immediately upstream of the UV reactor. UV after UF is the canonical placement for RO pre-treatment trains where biological contamination control is required.',
    });
  }
  if (d.roLifeYears < d.roMembrane.nominalLifeYears * 0.6) {
    recs.push({
      priority: 'high',
      title: 'Predicted membrane service life materially below nominal',
      body: `Estimated RO membrane life is ${d.roLifeYears.toFixed(1)} years against the manufacturer nominal of ${d.roMembrane.nominalLifeYears} years, a reduction of ${Math.round((1 - d.roLifeYears / d.roMembrane.nominalLifeYears) * 100)}%. ` +
            'The principal life-shortening drivers are residual iron, residual manganese and SDI carryover. Tightening any one of these upstream typically pays back inside two membrane replacement cycles. ' +
            'Recommended action: review the validation chips for each pre-treatment stage above and address any amber/red flags before commissioning. Consider a 30-day pilot trial on the actual feed water to verify the predicted membrane envelope is met.',
    });
  }
  if (d.meetsRO && recs.length === 0) {
    recs.push({
      priority: 'info',
      title: 'Train meets the RO design basis',
      body: `Every RO feed-water target (Fe <= ${RO_TARGETS.fe} mg/L, Mn <= ${RO_TARGETS.mn} mg/L, SDI <= ${RO_TARGETS.sdi}, TSS <= ${RO_TARGETS.tss} mg/L) is met with margin. ` +
            `Predicted RO membrane service life is ${d.roLifeYears.toFixed(1)} years, close to the manufacturer nominal of ${d.roMembrane.nominalLifeYears} years. ` +
            'To preserve this performance throughout the asset life, maintain operator KPI logging (Fe, SDI, ΔP across each stage, recovery) at the agreed frequency and follow the manufacturer cleaning regime for the catalytic and membrane stages.',
    });
  }
  if (d.meetsRO) {
    recs.push({
      priority: 'info',
      title: 'Recommended next steps to fabrication',
      body: 'Detailed engineering should now follow. Suggested deliverables: process flow diagram (PFD), piping and instrumentation diagram (P&ID), instrumentation schedule, hydraulic profile, electrical single-line, control philosophy and HAZOP. ' +
            'Confirm site geometry and access against the equipment footprint and delivery route. Issue a fixed-price request for quotation to at least two qualified suppliers. Arrange a factory acceptance test (FAT) protocol and a 30-day site performance verification period.',
    });
  }
  recs.push({
    priority: 'info',
    title: 'Operator monitoring recommendations',
    body: 'Install continuous logging for the following indicators at the points shown: inlet and outlet iron (colourimetric analyser), differential pressure across each filter, SDI15 at the RO feed point (weekly minimum), flow totaliser per train, backwash water volumes per cycle. ' +
          'Threshold alarms should trip rectifier and ramp dilution blower (for any electrolytic chlorine generator) when LEL hydrogen reaches 25% of the lower explosive limit, per IEC 60079 and NFPA 69.',
  });
  return recs;
}

/* ── MAIN GENERATOR ────────────────────────────────────────── */
export function generateTrainPdfReport(d: TrainReportInput): jsPDF {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const stamp = new Date(d.generatedAt);
  const projectCode = `RB-${stamp.getFullYear()}-${String(Math.abs([...d.trainName].reduce((s, c) => s + c.charCodeAt(0), 0)) % 100000).padStart(5, '0')}`;
  const dateStr = stamp.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
  const L = new Layout(doc, { trainName: d.trainName, projectCode, revision: 'R0', date: dateStr });

  /* ─── COVER PAGE ──────────────────────────────────────── */
  setFill(doc, C.INK); doc.rect(0, 0, PAGE_W, PAGE_H, 'F');
  setFill(doc, C.BRAND); doc.rect(0, 0, PAGE_W, 14, 'F');
  doc.setTextColor(15, 23, 42); setFont(doc, 'helvetica', 'bold', 10);
  doc.text('REYNOLDS & BAUHM LTD', M, 9);
  doc.setTextColor(255, 255, 255); setFont(doc, 'helvetica', 'normal', 8);
  doc.text(ascii('CONFIDENTIAL · ENGINEERING REPORT'), PAGE_W - M, 9, { align: 'right' });

  doc.setTextColor(255, 255, 255); setFont(doc, 'helvetica', 'normal', 9);
  doc.text(ascii('Process Engineering · Smart Tool 2.0'), M, 30);

  setFont(doc, 'helvetica', 'bold', 28);
  doc.text('Process Train', M, 78);
  doc.text('Engineering Report', M, 90);
  doc.setTextColor(203, 213, 225); setFont(doc, 'helvetica', 'normal', 14);
  const titleLines = doc.splitTextToSize(ascii(d.trainName), PAGE_W - 2 * M);
  doc.text(titleLines, M, 105);

  if (d.description) {
    setFont(doc, 'helvetica', 'italic', 10); doc.setTextColor(148, 163, 184);
    const descLines = doc.splitTextToSize(ascii(d.description), PAGE_W - 2 * M);
    doc.text(descLines, M, 130);
  }

  setFill(doc, '#1c2840'); doc.roundedRect(M, 170, PAGE_W - 2 * M, 86, 3, 3, 'F');
  doc.setTextColor(203, 213, 225); setFont(doc, 'helvetica', 'bold', 9);
  doc.text('DOCUMENT CONTROL', M + 5, 180);
  doc.setTextColor(255, 255, 255); setFont(doc, 'helvetica', 'normal', 9.5);
  const dcRows: Array<[string, string]> = [
    ['Project',         d.trainName],
    ['Project code',    projectCode],
    ['Revision',        'R0 — Issued for review'],
    ['Issue date',      dateStr],
    ['Customer',        d.customerName ?? '—'],
    ['Site',            d.siteName ?? '—'],
    ['Prepared by',     d.preparedBy ?? 'Reynolds & Bauhm Ltd'],
    ['Issued to',       d.preparedFor ?? '—'],
  ];
  dcRows.forEach(([k, v], i) => {
    doc.setTextColor(148, 163, 184); doc.text(ascii(k), M + 5, 190 + i * 7);
    doc.setTextColor(255, 255, 255);
    const vw = doc.splitTextToSize(ascii(v), PAGE_W - 2 * M - 50);
    doc.text(vw[0] ?? '', M + 50, 190 + i * 7);
  });

  const meetsBg = d.meetsRO ? C.GOOD : C.BAD;
  setFill(doc, meetsBg); doc.roundedRect(M, 268, PAGE_W - 2 * M, 14, 2, 2, 'F');
  doc.setTextColor(255, 255, 255); setFont(doc, 'helvetica', 'bold', 11);
  doc.text(d.meetsRO ? 'EFFLUENT MEETS RO DESIGN BASIS' : 'EFFLUENT BELOW RO DESIGN BASIS — REDESIGN REQUIRED', PAGE_W / 2, 277, { align: 'center' });

  setFill(doc, C.BRAND); doc.rect(0, PAGE_H - 10, PAGE_W, 10, 'F');
  doc.setTextColor(15, 23, 42); setFont(doc, 'helvetica', 'bold', 8);
  doc.text(ascii('REYNOLDS & BAUHM LTD · Co. 10436543 · Bridgwater, England'), PAGE_W / 2, PAGE_H - 4, { align: 'center' });

  /* ─── PAGE 2: Document Control & Scope ──────────────────── */
  doc.addPage(); L.pageNo = 2; L.startPage('Document Control & Scope');
  L.addHeading('1. Document control', 1);
  L.addParagraph(
    'This report constitutes the engineering basis for the process train named above. ' +
    'It is issued at revision R0 for review by the named recipient. All subsequent issues will carry an incremented revision suffix and a summary of changes recorded below.',
  );
  L.addTable(
    ['Rev', 'Date', 'Description', 'Prepared'],
    [['R0', dateStr, 'Issued for review', 'RB Smart Tool 2.0']],
    [18, 28, 100, 28],
  );
  L.addSpacer(3);
  L.addHeading('2. Distribution', 2);
  L.addBullet(`${d.preparedFor ?? '—'} (recipient for review)`);
  L.addBullet('Reynolds & Bauhm Ltd · project file');
  L.addBullet('Site engineering file');
  L.addSpacer(3);
  L.addHeading('3. Scope of this document', 2);
  L.addParagraph(
    'The report covers the engineering basis for the equipment train designed to deliver feed water of acceptable quality to the downstream reverse-osmosis (RO) plant. It is intended as an engineering reference for the customer, the operator, and any third-party engineer reviewing the design.',
  );
  L.addParagraph('It explicitly does NOT cover the following items, which are detailed-design deliverables produced after this concept is approved:');
  L.addBullet('Civil structures, slab design, bunding and containment');
  L.addBullet('Electrical single-line, MCC, motor schedules and earthing');
  L.addBullet('Instrumentation schedule and control philosophy');
  L.addBullet('HAZOP, LOPA, and DSEAR/ATEX assessment');
  L.addBullet('Factory acceptance test procedure and site acceptance test plan');
  L.addBullet('Operations & maintenance manuals and operator training');
  L.addBullet('Commercial terms — pricing, payment milestones, warranties (delivered separately as a commercial proposal)');

  /* ─── PAGE 3: Executive Summary ───────────────────────── */
  doc.addPage(); L.pageNo++; L.startPage('Executive Summary');
  L.addHeading('4. Executive summary', 1);
  const summaryParagraph =
    `This document sets out a ${d.stages.length}-stage pre-treatment train serving the downstream ${d.roMembrane.brand} ${d.roMembrane.model} reverse-osmosis membranes ` +
    `at the ${d.siteName ?? 'subject'} site. The train is sized for an influent flow of ${d.influent.flow.toFixed(1)} m3/h ` +
    `(${(d.influent.flow * 24).toFixed(0)} m3/day) and operates against the measured raw-water characteristics summarised in Section 7. ` +
    `Simulated final feed quality to the membranes is Fe ${d.finalWater.fe.toFixed(3)} mg/L, Mn ${d.finalWater.mn.toFixed(3)} mg/L and SDI15 ${d.finalWater.sdi.toFixed(1)} - ` +
    (d.meetsRO ? 'within' : 'outside') + ' the RO design envelope. Estimated membrane service life on this feed is ' +
    `${d.roLifeYears.toFixed(1)} years against a nominal ${d.roMembrane.nominalLifeYears} years.`;
  L.addParagraph(summaryParagraph);
  L.addSpacer(2);
  L.addHeading('5. Key performance indicators', 2);
  L.addTable(
    ['KPI', 'Target', 'Predicted', 'Status'],
    [
      ['Final Fe (mg/L)',       'less than or equal to 0.05', d.finalWater.fe.toFixed(3),        d.finalWater.fe <= 0.05 ? 'PASS' : 'FAIL'],
      ['Final Mn (mg/L)',       'less than or equal to 0.05', d.finalWater.mn.toFixed(3),        d.finalWater.mn <= 0.05 ? 'PASS' : 'FAIL'],
      ['Final SDI15',           'less than or equal to 3',    d.finalWater.sdi.toFixed(1),       d.finalWater.sdi <= 3   ? 'PASS' : 'FAIL'],
      ['Final TSS (mg/L)',      'less than or equal to 5',    d.finalWater.tss.toFixed(2),       d.finalWater.tss <= 5   ? 'PASS' : 'FAIL'],
      ['Final turbidity (NTU)', 'less than or equal to 1',    d.finalWater.turbidity.toFixed(3), d.finalWater.turbidity <= 1 ? 'PASS' : 'FAIL'],
      ['RO membrane life (yr)', `${d.roMembrane.nominalLifeYears} nominal`, `${d.roLifeYears.toFixed(1)}`, d.roLifeYears >= d.roMembrane.nominalLifeYears * 0.85 ? 'GOOD' : 'REVIEW'],
    ],
    [60, 38, 38, 28],
  );

  /* ─── PAGE 4: Design Basis & Influent ─────────────────── */
  doc.addPage(); L.pageNo++; L.startPage('Design Basis & Influent Characterisation');
  L.addHeading('6. Design basis', 1);
  L.addParagraph(
    `The downstream RO membrane specified for this study is the ${d.roMembrane.brand} ${d.roMembrane.model} (${d.roMembrane.classLabel}). ` +
    `The membrane published feed-water envelope sets the target outlet specification for the pre-treatment train:`,
  );
  L.addBullet(`Total iron less than or equal to ${d.roMembrane.feMaxMgL} mg/L (irreversible iron-oxide fouling above this level).`);
  L.addBullet(`Total manganese less than or equal to ${d.roMembrane.mnMaxMgL} mg/L.`);
  L.addBullet(`Silt density index SDI15 less than or equal to ${d.roMembrane.sdiMax} (colloidal fouling driver).`);
  L.addBullet(`Free chlorine less than or equal to ${d.roMembrane.freeChlorineMaxMgL} mg/L (polyamide hydrolysis hazard).`);
  L.addBullet('Operating temperature 5 to 45 deg C, pH 2 to 11 (cleaning excursions allowed).');
  L.addSpacer(3);
  L.addHeading('7. Influent water characterisation', 1);
  L.addParagraph(
    'The raw-water specification used as the simulation input is reproduced below. Where parameters were measured by an external laboratory, the report identifier is recorded in the customer file. Mn marked as a placeholder must be confirmed by laboratory test before media procurement.',
  );
  L.addTable(
    ['Parameter', 'Value', 'Units', 'Comment'],
    [
      ['Design flow',     d.influent.flow.toFixed(2),       'm3/h',           '24/7 continuous operation assumed'],
      ['Iron (total)',    d.influent.fe.toFixed(3),         'mg/L',           'Dissolved Fe(II) assumed unless aerated upstream'],
      ['Manganese',       d.influent.mn.toFixed(3),         'mg/L',           'Laboratory test recommended if unconfirmed'],
      ['pH',              d.influent.ph.toFixed(2),         '—',              'Operating range — affects oxidation kinetics'],
      ['Alkalinity',      d.influent.alkalinity.toFixed(0), 'mg/L as CaCO3',  'Buffering capacity for oxidation reaction'],
      ['Hardness',        d.influent.hardness.toFixed(0),   'mg/L as CaCO3',  'RO scaling indicator'],
      ['TSS',             d.influent.tss.toFixed(2),        'mg/L',           ''],
      ['Turbidity',       d.influent.turbidity.toFixed(3),  'NTU',            ''],
      ['SDI15',           d.influent.sdi.toFixed(1),        '—',              'Baseline; pre-treatment must drop to ≤ 3'],
      ['Dissolved O2',    d.influent.doIn.toFixed(2),       'mg/L',           'Drives Fe(II) oxidation kinetics'],
      ['Temperature',     d.influent.temp.toFixed(1),       'deg C',          'Affects viscosity, flux and oxidation rate'],
    ],
    [40, 28, 32, 60],
  );

  /* ─── PROCESS DESCRIPTION ─────────────────────────────── */
  doc.addPage(); L.pageNo++; L.startPage('Process Description');
  L.addHeading('8. Process description', 1);
  L.addParagraph(
    `Raw water from the source is routed sequentially through ${d.stages.length} treatment stage${d.stages.length === 1 ? '' : 's'} ` +
    'before entering the downstream RO membrane bank. Each stage transforms the water in a specific, complementary way; the cumulative effect must satisfy the RO membrane feed envelope set out in Section 6. ' +
    'The stages are described in flow order below. Each subsection includes the engineering basis (equations and correlations used to size the equipment), the live cascade outcome, the equipment configuration, and a physics-validation summary.',
  );

  d.stages.forEach((stage, i) => {
    L.addSpacer(2);
    L.addHeading(`8.${i + 1}  Stage ${stage.order} — ${stageTitle(stage.key)}`, 2);
    const purpose = stagePurpose(stage.key);
    if (purpose) L.addParagraph(purpose);

    /* ── Engineering equations ── */
    const eqs = stageEquations(stage.key);
    if (eqs.length) {
      L.addHeading('Engineering equations and correlations', 3);
      eqs.forEach(eq => L.addEquation(eq.label, eq.expression, eq.legend));
    }

    /* ── Live cascade outcome ── */
    L.addHeading('Performance on this feed (cascade simulation)', 3);
    L.addTable(
      ['Parameter', 'Inlet', 'Outlet', 'Delta'],
      [
        ['Iron (mg/L)',      (stage.inWater.fe ?? 0).toFixed(3),        (stage.outWater.fe ?? 0).toFixed(3),        ((stage.outWater.fe ?? 0) - (stage.inWater.fe ?? 0)).toFixed(3)],
        ['Manganese (mg/L)', (stage.inWater.mn ?? 0).toFixed(3),        (stage.outWater.mn ?? 0).toFixed(3),        ((stage.outWater.mn ?? 0) - (stage.inWater.mn ?? 0)).toFixed(3)],
        ['SDI15',            (stage.inWater.sdi ?? 0).toFixed(1),       (stage.outWater.sdi ?? 0).toFixed(1),       ((stage.outWater.sdi ?? 0) - (stage.inWater.sdi ?? 0)).toFixed(1)],
        ['Turbidity (NTU)',  (stage.inWater.turbidity ?? 0).toFixed(2), (stage.outWater.turbidity ?? 0).toFixed(2), ((stage.outWater.turbidity ?? 0) - (stage.inWater.turbidity ?? 0)).toFixed(2)],
        ['TSS (mg/L)',       (stage.inWater.tss ?? 0).toFixed(1),       (stage.outWater.tss ?? 0).toFixed(1),       ((stage.outWater.tss ?? 0) - (stage.inWater.tss ?? 0)).toFixed(1)],
      ],
      [60, 36, 36, 32],
    );

    if (stage.notes) {
      L.addParagraph(stage.notes, { italic: true, color: C.MUTED, size: 8.5 });
    }

    /* ── Configuration ── */
    L.addHeading('Equipment configuration', 3);
    stage.paramsSummary.slice(0, 12).forEach(({ label, value }) => L.addKeyValueRow(label, value));

    /* ── Validation ── */
    if (stage.validation.length) {
      L.addHeading('Physics validation', 3);
      stage.validation.forEach(v => {
        const colour = v.status === 'pass' ? C.GOOD : v.status === 'warn' ? C.WARN : C.BAD;
        const sym = v.status === 'pass' ? 'PASS' : v.status === 'warn' ? 'WARN' : 'FAIL';
        L.addKeyValueRow(`[${sym}] ${v.label}`, v.detail, { valueColor: colour, bold: true });
      });
    }
  });

  /* ─── MATERIAL BALANCE ───────────────────────────────── */
  doc.addPage(); L.pageNo++; L.startPage('Material Balance');
  L.addHeading('9. Cumulative material balance', 1);
  L.addParagraph(
    'The table below traces the key water-quality parameters through every enabled stage of the train. Each row is the cumulative outlet after that stage. This is the audit trail used when the downstream RO membrane bank is benchmarked against the design envelope.',
  );
  const balRows: string[][] = [];
  balRows.push(['Influent', d.influent.fe.toFixed(3), d.influent.mn.toFixed(3), d.influent.sdi.toFixed(1), d.influent.turbidity.toFixed(2), d.influent.tss.toFixed(1)]);
  d.stages.forEach((s, i) => {
    balRows.push([`After stage ${i + 1}`, (s.outWater.fe ?? 0).toFixed(3), (s.outWater.mn ?? 0).toFixed(3), (s.outWater.sdi ?? 0).toFixed(1), (s.outWater.turbidity ?? 0).toFixed(2), (s.outWater.tss ?? 0).toFixed(1)]);
  });
  L.addTable(
    ['Stream', 'Fe mg/L', 'Mn mg/L', 'SDI15', 'Turbidity NTU', 'TSS mg/L'],
    balRows,
    [42, 26, 26, 22, 32, 26],
  );

  /* ─── RECOMMENDATIONS ─────────────────────────────────── */
  doc.addPage(); L.pageNo++; L.startPage('Recommendations & Risk');
  L.addHeading('10. Engineering recommendations', 1);
  L.addParagraph(
    'The recommendations below are diagnosed automatically from the simulation outcome. Each carries a priority tag and a prose body explaining the reasoning and the suggested action.',
  );
  L.addSpacer(2);
  const recs = buildRecommendations(d);
  recs.forEach((rec, i) => {
    const tone: 'good' | 'warn' | 'bad' | 'neutral' =
      rec.priority === 'critical' ? 'bad' :
      rec.priority === 'high'     ? 'warn' :
      rec.priority === 'medium'   ? 'warn' :
      'neutral';
    L.addPanel(`R${i + 1} — [${rec.priority.toUpperCase()}] ${rec.title}`, rec.body, tone);
  });

  L.addSpacer(2);
  L.addHeading('11. Risk register', 1);
  L.addParagraph('The principal risks identified during simulation are listed in priority order. Mitigations should be carried through to the detailed-design HAZOP.');
  L.addTable(
    ['Risk', 'Likelihood', 'Impact', 'Mitigation'],
    [
      ['Influent Fe spike above 0.5 mg/L during seasonal variation', 'M', 'H', 'Online Fe meter; auto KMnO4 dose ramp; downstream cartridge as safety net'],
      ['Catalytic media KMnO4 carryover (pink-water risk to RO)',    'L', 'H', 'KMnO4 injection sized to demand; cartridge polish downstream catches breakthrough'],
      ['UF fibre fouling from upstream Fe(III) floc',                 'M', 'H', 'Cartridge polish before UF; CEB frequency log; quarterly CIP cycle'],
      ['Backwash water supply inadequate',                            'M', 'M', 'Dedicated backwash break tank (15-20 m3) drawn from raw-water buffer'],
      ['Free chlorine arrival on RO without warning',                 'L', 'C', 'GAC pre-RO; redundant ORP monitor on RO feed line; auto rectifier trip'],
      ['Manganese above expected (untested)',                         'M', 'M', 'Confirm Mn by laboratory test before media procurement'],
      ['Power failure / rectifier trip during shock dosing',          'L', 'M', 'UPS on instrumentation; auto-shutdown sequence; hypochlorite storage covers downtime'],
    ],
    [78, 22, 22, 58],
  );

  /* ─── KONRAD / DYNAMIK FILTR VENDOR-QUOTE ASSESSMENT ──── */
  doc.addPage(); L.pageNo++; L.startPage('Vendor Quote Assessment - Dynamik Filtr');
  L.addHeading('12. Comparative assessment of the vendor-proposed configuration', 1);
  L.addParagraph(
    'A process proposal has been received from Dynamik Filtr Nocon i Wspolnicy S.J. (Bor 139, Czestochowa, Poland), via Konrad Sliwinski (Director). An initial quote of EUR 70,000 EXW was issued on 26 May 2026; a materially revised process scope followed on 28 May 2026. This assessment is written against the REVISED scope, which is a significant improvement on the first quote. Pricing for the revised scope has not yet been issued - Konrad has asked for agreement on the process concept before he calculates values. The sections below set out a side-by-side engineering critique against the recommended Reynolds & Bauhm design, highlighting where the revised proposal is now sound, where concerns remain, and the open items that must be closed before a price is accepted.',
  );

  L.addHeading('12.1  Revised process scope (28 May 2026)', 2);
  L.addBullet('Pressurised aeration: 1 x DN1600 aerator, plus valves and pipework. Vendor requests the raw-water inlet pressure is raised to 4 bar.');
  L.addBullet('First-stage filters: 2 x DN1600, anthracite cap over Multisand MnO2 catalytic media, combined bed depth max 1200 mm, plus valves and pipework.');
  L.addBullet('Second-stage polishing filters: 2 x DN1600 (media not yet specified), plus valves and pipework.');
  L.addBullet('Backwash supply: vendor proposes reusing the EXISTING 200 m3 tank (located after the filters) rather than supplying a new break tank. Approx. 25 m3 of water per single-filter backwash.');
  L.addBullet('KMnO4 dosing station: NOT required for the selected Multisand MnO2 media (natural manganese-dioxide ore, regenerated by dissolved oxygen from the aeration stage).');
  L.addBullet('Can additionally include: air compressor, backwash pump, air blower. External interconnection (tank-to-backwash-pump piping) cannot yet be priced.');
  L.addBullet('Installation: Dynamik Filtr has its own installation crew in Scotland; pricing requires the project documentation first.');
  L.addBullet('The offer will be subject to revision during the detailed-design (project documentation) phase.');

  L.addHeading('12.2  Where the revised proposal is now sound', 2);
  L.addPanel(
    'A1 - Aeration is now in scope (was the biggest gap in the first quote)',
    'The DN1600 pressurised aerator provides the dissolved oxygen needed to oxidise Fe(II) to filterable Fe(III) floc, and to regenerate the catalytic media. This directly closes the principal deficiency of the original quartz-only quote.',
    'good',
  );
  L.addPanel(
    'A2 - Multisand MnO2 catalytic media with no KMnO4 dependency',
    'Multisand MnO2 is a natural manganese-dioxide catalytic ore (functionally comparable to Pyrolusite/Birm). With adequate dissolved oxygen from the aeration stage it oxidises and captures both Fe and Mn and regenerates without continuous potassium-permanganate dosing. This is a genuine operating-cost advantage over a Greensand-Plus + KMnO4 scheme: no permanganate consumable, no pink-water carryover risk, and lower operator chemical handling. The anthracite cap provides depth-filtration headroom and extends run length between backwashes.',
    'good',
  );
  L.addPanel(
    'A3 - Two-stage filtration plus auxiliaries and a UK installation crew now offered',
    'A second polishing filtration stage, plus the option to include the compressor, backwash pump and blower, and an in-country (Scotland) installation team, together move the proposal from bare factory-gate vessels towards a more complete delivery. This is a substantial commercial improvement on the EXW-only first quote.',
    'good',
  );

  L.addHeading('12.3  Open concerns and items still to be closed', 2);
  L.addPanel(
    'C1 - The 4 bar inlet-pressure request conflicts with the available site pressure',
    'The vendor asks that the raw-water inlet be raised to 4 bar. The site data records only about 2 barg available at the proposed tie-in, while the borehole pump itself delivers 70 m3/h at 9 barg. Running a pressurised aeration + two filtration stages in series at 4 bar therefore implies either (a) tapping the borehole pump upstream of the existing pressure break, or (b) installing a dedicated booster pump set. This is a material hydraulic question with cost and control implications. Action: confirm the exact pressure available at the filter-station tie-in, the head loss budget across aerator + 2 filter stages at 24 m3/h, and who supplies any booster pump. A pressurised aerator at 4 bar also reduces oxygen-transfer efficiency versus an atmospheric cascade - confirm the vendor guarantees the post-aeration dissolved-oxygen and the Fe oxidation it implies.',
    'bad',
  );
  L.addPanel(
    'C2 - Vessel material of construction is still not stated',
    'The revised scope does not state whether the DN1600 vessels are painted carbon steel (as the first quote) or stainless / lined. With an aerated, oxygen-rich, Fe/Mn-laden duty - and periodic shock chlorination - painted carbon steel is a long-term corrosion liability once the internal coating is breached. Action: require a written material specification (304L, or lined CS with coating type, DFT, supplier and expected service life) before any commitment.',
    'bad',
  );
  L.addPanel(
    'C3 - Polishing-stage media is unspecified - SDI to RO is not yet guaranteed',
    'The borehole PSD report records 47% of particles below 1 micron with the d10-by-volume at 1.24 micron. Granular media (anthracite/Multisand) has an effective cut-off in the 10-20 micron band and will not, by itself, deliver SDI15 < 3 to the RO. The second "polishing" stage could close this gap only if it is an absolute-rated barrier (fine cartridge, or ideally ultrafiltration). As currently described - another granular filter - it will not reliably achieve SDI < 3. Action: ask the vendor to state the polishing media and the GUARANTEED SDI15 at the RO feed; if it is granular, a downstream cartridge or UF barrier is still required.',
    'warn',
  );
  L.addPanel(
    'C4 - Reusing the existing 200 m3 post-filter tank as a backwash source needs hydraulic and quality checks',
    'Reusing the existing tank avoids a new break tank, which is sensible IF the hydraulics work. But the tank sits downstream of the filters and feeds the RO; drawing 25 m3 per backwash from it competes with RO demand and must not let backwash return contaminate stored treated water. Action: confirm tank turnover and level control during a backwash, the backwash flow and duration per vessel, and that the tank-to-backwash-pump interconnect (which the vendor states it cannot yet price) is in someone\'s scope. Verify the air-scour + water backwash sequence achieves the required bed expansion at the available backwash rate.',
    'warn',
  );
  L.addPanel(
    'C5 - Manganese is assumed, not measured; free chlorine to RO is not addressed; price not yet issued',
    'The vendor sizes the Multisand for manganese on assumption ("we do not know if Mn is present - we assume yes"). This is prudent, but a laboratory Mn test on the actual feed should still confirm the media loading. Separately, nothing in the scope addresses free-chlorine removal ahead of the polyamide RO membranes (a GAC bed or SBS dosing) if shock chlorination is retained. Finally, no price has yet been issued for the revised scope - the comparison remains provisional until Dynamik Filtr returns calculated values and a firm material/SDI specification.',
    'warn',
  );

  L.addHeading('12.4  Point-by-point status against the RO design basis', 2);
  L.addTable(
    ['Design-basis item',                              'Required',          'Revised DF proposal', 'Status'],
    [
      ['Upstream aeration (Fe oxidation)',             'Required',          'DN1600 pressurised aerator included',             'OK'],
      ['Catalytic Fe/Mn media',                        'Required',          'Multisand MnO2 + anthracite, 2 vessels',          'OK'],
      ['KMnO4 dosing / pink-water risk',               'Avoid if possible', 'Not required (natural MnO2 ore)',                 'OK'],
      ['Total iron at RO feed',                        '<= 0.05 mg/L',      'Plausible once 4 bar / DO confirmed',             'CONFIRM'],
      ['Total manganese at RO feed',                   '<= 0.05 mg/L',      'Mn assumed, not lab-confirmed',                   'CONFIRM'],
      ['SDI15 at RO feed',                             '<= 3',              'Polishing media unspecified - not guaranteed',    'GAP'],
      ['Free chlorine at RO feed',                     '<= 0.1 mg/L',       'Not addressed in scope',                          'GAP'],
      ['Inlet pressure / hydraulics',                  '~2 barg available', 'Vendor requests 4 bar - booster needed',          'GAP'],
      ['Vessel material (oxidising service)',          '304L or lined CS',  'Not stated in revised scope',                     'CONFIRM'],
      ['Backwash pump + blower + compressor',          'Required',          'Can be included (price pending)',                 'OK'],
      ['Backwash water source',                        'Required',          'Reuse existing 200 m3 tank (checks needed)',      'CONFIRM'],
      ['Site installation',                            'Required',          'DF crew in Scotland (needs project docs)',        'OK'],
      ['Performance guarantee',                        'Required',          'Subject to detailed-design revision',             'CONFIRM'],
      ['Firm price for revised scope',                 'Required',          'Not yet issued',                                  'GAP'],
    ],
    [54, 34, 64, 22],
  );

  L.addHeading('12.5  Engineering conclusion', 2);
  L.addParagraph(
    'The 28 May revised proposal is a major step forward from the original EUR 70,000 quartz-only quote. With pressurised aeration, Multisand MnO2 catalytic media (no KMnO4), a two-stage filter arrangement, optional auxiliaries and a Scotland-based installation crew, Dynamik Filtr is now offering something close to a complete iron/manganese removal station. The choice of a natural MnO2 ore that needs no permanganate dosing is a genuine operating-cost strength.',
  );
  L.addParagraph(
    'However, the proposal cannot yet be accepted on engineering grounds because four items are unresolved: (1) the 4 bar inlet-pressure request against roughly 2 barg available on site - a real hydraulic problem requiring a booster pump decision; (2) the vessel material of construction is unstated; (3) the polishing-stage media is unspecified, so SDI15 < 3 to the RO is not guaranteed and a cartridge or UF barrier may still be needed; and (4) no firm price has been issued for the revised scope. Manganese should also be confirmed by laboratory test, and free-chlorine removal ahead of the RO should be clarified if shock chlorination is retained.',
  );
  L.addParagraph(
    'Recommended position: confirm agreement with the process concept (aeration + Multisand MnO2 + polishing), and in the same response require written answers on inlet pressure / booster, vessel material, polishing media with a guaranteed RO-feed SDI, and the backwash-tank interconnect scope - then ask Dynamik Filtr to issue calculated values. The Reynolds & Bauhm options in the separate Pricing tab remain the benchmark: Option B/C explicitly guarantee SDI to the RO (via cartridge/UF), state 304L construction, and carry a performance guarantee, which the DF proposal does not yet match.',
  );

  /* ─── APPENDIX A: Optional Siemens S7-1500 automation ──── */
  {
    // Derive an indicative I/O schedule + PLC tier from the enabled stages.
    let di = 8, dou = 6, ai = 4, ao = 2; // base (incomer, common alarms, flow)
    let hasUF = false, hasUV = false, hasDose = false;
    d.stages.forEach(s => {
      if (s.key === 'aeration_column') { di += 4; dou += 3; ai += 2; ao += 1; }
      if (s.key === 'cascade_aerator') { di += 2; dou += 1; ai += 1; }
      if (s.key === 'greensand' || s.key === 'multimedia' || s.key === 'dual_media_filter' || s.key === 'sand_filter_single' || s.key === 'gac_filter') { di += 8; dou += 6; ai += 3; ao += 1; }
      if (s.key === 'cartridge_polish') { di += 2; ai += 1; }
      if (s.key === 'pacl_dosing') { dou += 2; ao += 1; hasDose = true; }
      if (s.key === 'uf') { di += 12; dou += 10; ai += 5; ao += 2; hasUF = true; }
      if (s.key === 'uv_disinfection') { di += 4; dou += 2; ai += 2; hasUV = true; }
    });
    if (d.stages.some(s => s.key === 'greensand')) hasDose = true; // KMnO4 regen
    const premium = hasUF || hasUV;
    doc.addPage(); L.pageNo++; L.startPage('Appendix A — Automation (Siemens S7-1500)');
    L.addHeading('Appendix A — Optional automation (Siemens SIMATIC S7-1500)', 1);
    L.addParagraph(
      'The control scope below is an OPTIONAL, fully-engineered automation package for this train, derived from the enabled stages above. ' +
      'It is presented as an appendix so it can be included or omitted from the commercial scope. ' +
      (premium
        ? 'Because this train includes membrane and/or UV stages, a hot-standby S7-1500H redundant controller is recommended for maximum availability.'
        : 'A single S7-1500 controller is sufficient for this train; a hot-standby pair is available as an upgrade if continuous availability is critical.'),
    );
    L.addHeading('Platform', 2);
    L.addBullet(premium ? 'Siemens SIMATIC S7-1500H hot-standby redundant CPU pair (TIA Portal v18)' : 'Siemens SIMATIC S7-1500 PLC (CPU 1515-2 PN, TIA Portal v18)');
    L.addBullet('SIMATIC HMI Comfort Panel (12-inch) with WinCC Unified — mnemonics, trends, alarms, 30-day historian');
    L.addBullet('ET 200SP distributed I/O on a managed PROFINET ring (Scalance switches)' + (premium ? ' with media redundancy (MRP)' : ''));
    L.addBullet('MCC with SINAMICS G120C variable-speed drives; SITOP UPS for instrument ride-through');
    L.addBullet('OPC UA server + volt-free OK/Alarm contacts to the site BMS/DCS' + (premium ? '; secure outbound cellular VPN for remote diagnostics' : ''));
    L.addHeading('Indicative I/O schedule', 2);
    L.addTable(
      ['I/O type', 'Channels', 'Typical signals'],
      [
        ['Digital inputs (DI)',  String(di),  'valve limits, level switches, run/fault status'],
        ['Digital outputs (DO)', String(dou), 'valve solenoids, pump/blower starts, lamp tower'],
        ['Analogue inputs (AI)', String(ai),  'flow, pressure, pH/ORP, turbidity, iron, dP, level'],
        ['Analogue outputs (AO)',String(ao),  'VFD speed references, dosing-pump setpoints'],
      ],
      [50, 28, 96],
    );
    L.addHeading('Control philosophy', 2);
    L.addBullet('Auto / Manual / Maintenance modes per item, key-switched on the HMI.');
    L.addBullet('Filter backwash on differential pressure, throughput or timer (first to occur), staggered with anti-coincidence interlock.');
    if (hasDose) L.addBullet('Permanganate / coagulant dosing proportional to treated-water flow, trimmed on the online iron reading where fitted.');
    if (hasUF) L.addBullet('UF filtration/backwash/CEB/CIP sequencing with TMP- and time-based scheduling.');
    if (hasUV) L.addBullet('UV dose-pacing on flow and UVT with low-dose and lamp end-of-life trips.');
    L.addBullet('Safety interlocks (F-PLC where assessed): E-stop ring, high-high tank level, low-low suction pressure.');
    L.addBullet('Daily KPI report (iron, dP trends, backwash counts, chemical use) generated by the historian.');
    L.addHeading('Compliance', 2);
    L.addBullet('Machinery Directive / UKCA and BS EN 60204-1 (electrical safety of machinery)');
    L.addBullet('Control cabinet to BS EN 61439; EMC per BS EN 61000');
    L.addBullet('Functional-safety interlocks to BS EN 62061 / SIL where assessed');
    L.addBullet('Cyber-security baseline to IEC 62443-3-3 (SL-2): role-based logins, signed firmware, audited remote access');

  /* ─── REFERENCES ─────────────────────────────────────── */
  }
  doc.addPage(); L.pageNo++; L.startPage('Standards & References');
  L.addHeading('13. Standards and correlations used', 1);
  L.addParagraph('All sizing calculations and performance correlations in this report are drawn from peer-reviewed engineering literature and from manufacturer published data. The principal references are listed below.');
  const refs: Array<[string, string]> = [
    ['Granular media filtration',  'Cleasby J.L. & Logsdon G.S. — Granular Bed and Precoat Filtration, AWWA Water Quality & Treatment, 6th edition'],
    ['Backwash hydraulics',        'Amirtharajah A. & Wetstein D.P. — Initial degradation of effluent quality during filter cycles, JAWWA, 1980'],
    ['Filtration envelope',        'USEPA Filtration Guidance Manual — surface-water treatment rules'],
    ['Ergun head loss',            'Ergun S., Fluid flow through packed columns, Chemical Engineering Progress, 48(2), 1952'],
    ['Fe(II) oxidation kinetics',  'Stumm W. & Lee G.F. — Oxygenation of ferrous iron, Industrial & Engineering Chemistry, 53, 1961'],
    ['DO saturation polynomial',   'Benson B.B. & Krause D. — The concentration and isotopic fractionation of oxygen dissolved in freshwater and seawater, Limnology & Oceanography, 1984'],
    ['Cascade aerator deficit ratio', 'Twort, Ratnayaka, Brandt — Water Supply, 7th edition; Gameson correlation'],
    ['UV disinfection dose',       'USEPA UVDGM 2006 — UV Disinfection Guidance Manual (EPA-815-R-06-007)'],
    ['UF flux and temperature',    'AWWA M53 — Microfiltration and Ultrafiltration Membranes for Drinking Water; Crittenden et al., MWH 3rd edition'],
    ['GAC adsorption',             'USEPA EPA-815-R-99-014 — alternative DBP control options including GAC'],
    ['Hydrogen vent safety',       'IEC 60079-20-1 (LEL of H2 in air = 4% v/v), NFPA 69 (concentration control by ventilation), DSEAR (UK)'],
    ['RO membrane envelopes',      'Manufacturer element data sheets — Hydranautics, DuPont (FilmTec), Toray, LG Chem, Suez/Veolia'],
  ];
  L.addTable(['Topic', 'Reference'], refs, [55, 125]);

  L.addSpacer(4);
  L.addHeading('14. Limitations and disclaimer', 1);
  L.addParagraph(
    'The simulation outputs in this document are estimates produced by industry-standard correlations applied to the live influent data and the stage configurations recorded in Sections 6 to 8. They are intended to support feasibility decisions, customer alignment and supplier selection. They are not warranties of equipment performance. ' +
    'Final guarantees must be issued by the equipment vendor following pilot validation and a witnessed factory acceptance test. Reynolds & Bauhm Ltd accepts no liability for decisions taken on the basis of this preliminary report without subsequent detailed engineering. ' +
    'This report deliberately omits commercial information (pricing, payment terms, warranties) — these are delivered separately as a commercial proposal.',
    { italic: true, size: 8.5, color: C.MUTED },
  );

  // Rewrite the "Page X of Y" stamp on every page now that we know the final total.
  // Pages 2+ have a header at y=24, top-right. Cover page (page 1) has no such stamp.
  const total = doc.getNumberOfPages();
  for (let p = 2; p <= total; p++) {
    doc.setPage(p);
    setFill(doc, '#ffffff'); doc.rect(PAGE_W - M - 32, 21, 32, 4.5, 'F');
    setText(doc, C.MUTED); setFont(doc, 'helvetica', 'normal', 8);
    doc.text(ascii(`Page ${p} of ${total}`), PAGE_W - M, 24, { align: 'right' });
  }
  L.totalPages = total;
  return doc;
}
